import { useState } from "react";
import { Grid, Stack, Typography, Button, IconButton, CircularProgress, Chip, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import QuizIcon from "../../../../../../assets/icons/quiz-icon-overview.svg";
import { StyledPaper } from "../../../../../customComponents/styled/styledPaper";
import MathContent from "../../../../../customComponents/MathContent";
import {
  LessonPlanLabel,
  LessonPlanValue,
} from "../../../../../customComponents/styled/StyledTypgraphies";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetQuizByIdQuery } from "../../../../../../app/quizApiSlice";
import ManageQuizModal from "./ManageQuizModal";
import PrintQuizModal from "./PrintQuizModal";

const ViewQuiz = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [manageOpen, setManageOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [isInstructionExpanded, setIsInstructionExpanded] = useState(false);

  const { data: apiData, isLoading } = useGetQuizByIdQuery({ id, include_participants: false }, {
    skip: !id,
  });

  // Use API data if available, fall back to location state
  const quiz = apiData?.quiz || location.state?.cardData || null;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!quiz) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No quiz data found. Please navigate from the quiz list.
        </Typography>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };
  const instructionText = quiz?.instruction?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || "";
  const hasInstruction = Boolean(instructionText);
  const isLongInstruction = instructionText.length > 100;

  return (
    <Box>
      <Stack gap={4} key={quiz?.id}>
        {/* Header with back button and manage */}
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 1.5 : 0}
        >
          <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: "wrap" }}>
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <img src={QuizIcon} alt="" style={{ height: "24px", width: "24px" }} />
            <Typography
              sx={{
                color: "text.primary",
                fontSize: isMobile ? "18px" : "23.813px",
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              {quiz?.questions?.[0]?.topic} Quiz
            </Typography>
            {quiz?.is_offline && (
              <Chip
                icon={<WifiOffIcon sx={{ fontSize: 14 }} />}
                label="Offline"
                size="small"
                variant="outlined"
                color="default"
                sx={{ ml: 0.5 }}
              />
            )}
          </Stack>
          <Stack direction="row" gap={1} sx={{ width: isMobile ? "100%" : "auto", justifyContent: isMobile ? "flex-end" : "initial" }}>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setManageOpen(true)}
              size={isMobile ? "small" : "medium"}
            >
              Manage
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate(`/lms/quizzes/report-list/${id}/submissions`)}
              size={isMobile ? "small" : "medium"}
            >
              Report
            </Button>
            {!isMobile && (
              <Button
                variant="outlined"
                onClick={() => setPrintOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Print
              </Button>
            )}
          </Stack>
        </Stack>

        <StyledPaper sx={{ py: isMobile ? 2 : 4, px: isMobile ? 2 : 8 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              rowGap: isMobile ? 2 : 4,
              columnGap: isMobile ? 2 : 0,
              ">*": {
                width: isMobile ? "calc(50% - 8px)" : "33%",
              },
            }}
          >
            <Stack>
              <LessonPlanLabel>Date</LessonPlanLabel>
              <LessonPlanValue>{formatDate(quiz?.updated_at)}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Topic</LessonPlanLabel>
              <LessonPlanValue>{quiz?.questions?.[0]?.topic}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Subject</LessonPlanLabel>
              <LessonPlanValue>{quiz?.subject}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Class</LessonPlanLabel>
              <LessonPlanValue>{quiz?.level}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Questions</LessonPlanLabel>
              <LessonPlanValue>{quiz?.questions?.length}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Duration</LessonPlanLabel>
              <LessonPlanValue>{quiz?.duration} mins</LessonPlanValue>
            </Stack>
          </Box>
        </StyledPaper>
        {hasInstruction && (
          <StyledPaper sx={{ py: isMobile ? 2 : 3, px: isMobile ? 2 : 8 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "16px" : "18px" }}>
                Instructions
              </Typography>
              {isLongInstruction && (
                <Button
                  size="small"
                  endIcon={isInstructionExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => setIsInstructionExpanded((current) => !current)}
                  sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
                >
                  {isInstructionExpanded ? "Show less" : "Show more"}
                </Button>
              )}
            </Box>
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.7,
                display: "-webkit-box",
                WebkitLineClamp: isLongInstruction && !isInstructionExpanded ? 2 : "unset",
                WebkitBoxOrient: "vertical",
                overflow: isLongInstruction && !isInstructionExpanded ? "hidden" : "visible",
                "& p": { m: 0 },
                "& p + p": { mt: 1 },
                "& ul, & ol": { my: 0, pl: 3 },
                "& img": { maxWidth: "100%" },
              }}
            >
              <MathContent html={quiz.instruction} />
            </Typography>
          </StyledPaper>
        )}
        {quiz?.questions?.map((question, i) => (
          <StyledPaper
            sx={{
              py: isMobile ? 2 : 4,
              px: isMobile ? 2 : 8,
              display: isMobile ? "block" : "flex",
              alignItems: "flex-start",
              gap: isMobile ? 2 : 6,
            }}
            key={question?.id}
          >
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "white",
                width: "fit-content",
                px: "20px",
                py: "15px",
                borderRadius: "72px",
                fontWeight: 700,
                fontSize: isMobile ? "16px" : "20px",
                mb: isMobile ? 1.5 : 0,
              }}
            >
              {i + 1}
            </Box>
            <Grid container rowSpacing={3} columnSpacing={4}>
              <Grid item xs={12}>
                <Box
                  sx={{ p: 1, fontSize: "16px", "& p": { margin: 0 }, "& img": { maxWidth: "100%" } }}
                >
                  <MathContent html={question?.body} />
                </Box>
              </Grid>
              {question?.options?.map((option) => (
                <Grid key={option?.id} item xs={12} md={6}>
                  <Box
                    sx={(theme) => ({
                      border: "1px solid",
                      borderColor: option?.is_answer ? "#27AE60" : "#E02424",
                      borderRadius: "14px",
                      padding: "17px",
                      fontWeight: 500,
                      color: option?.is_answer ? "#27AE60" : theme.palette.text.primary,
                    })}
                  >
                    <Box
                      sx={{ "& p": { margin: 0 }, "& img": { maxWidth: "100%" } }}
                    >
                      <MathContent html={option.body} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </StyledPaper>
        ))}
      </Stack>

      {manageOpen && (
        <ManageQuizModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          quiz={quiz}
          onDeleted={() => navigate(-1)}
          onUpdated={() => setManageOpen(false)}
        />
      )}

      {printOpen && (
        <PrintQuizModal
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          quiz={quiz}
        />
      )}
    </Box>
  );
};

export default ViewQuiz;
